import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { FormProvider, useForm } from 'react-hook-form';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { ExamGridModel, FileModel, TagModel, WorklistViewModel } from 'models';

import { MOBILE_BREAKPOINT, useMobileDetection } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { ProgressSpinnerOverlay } from 'core/ui';

import { useApiClient, useApiQuery } from 'features/api';
import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { useSignalRRefresh } from 'features/auth/hooks/useSignalRRefresh';
import { DocumentCategories } from 'features/exam/constants';
import { useSessionLocation } from 'features/location';
import { FileUploadMetadata, Upload } from 'features/media/fragments/Upload';
import { ShareType } from 'features/patient/constants';
import { useFormSubmissionsQuery } from 'features/patient/hooks/useFormSubmissionsQuery';
import { useMergedTags } from 'features/tags/hooks/useMergedTags';
import { QRScanModal } from 'features/upload-exams/fragments/QRScanModal';
import { UploadPipelineProvider } from 'features/upload-pipeline/fragments/UploadPipelineProvider';
import { WorklistSidebar } from 'features/worklist/fragments/WorklistSidebar';
import { WorklistSidebarCollapsed } from 'features/worklist/fragments/WorklistSidebarCollapsed';
import { determineWorklistSidebarState } from 'features/worklist/utils';

import { useCasesQuery } from '../../case/hooks/useCasesQuery';
import { FileViewerProvider, useFileViewerContext } from '../contexts/FileViewerContext';
import { useFileFiltering } from '../hooks/useFileFiltering';
import { useFileGrouping } from '../hooks/useFileGrouping';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import { usePatientFileData } from '../hooks/usePatientFileData';
import { useTagSidebarData } from '../hooks/useTagSidebarData';
import { PatientRoutes } from '../routes';
import { getAllDescendantTagIds, processTagData } from '../services/tagHelperUtils';
import { getTagPath, slugify } from '../services/worklistNavigationService';
import { PatientFilePreviewEdit } from './PatientFilePreviewEdit';
import { PreparedFile, SortCriteria } from './PatientFiles.types';
import { PatientFilesHeader } from './PatientFilesHeader';
import { PatientFilesStackedView } from './PatientFilesStackedView';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

interface PatientFilesProps {
  className?: string;
  patientId: number;
  gridData?: ExamGridModel[];
  onViewFile?: (file: FileModel, context: FileModel[]) => void;
  onUploadModeChange?: (isUploadMode: boolean) => void;
  shareContext?: {
    shareLinkId: string;
    isShareMode: true;
  };
  isReadOnly?: boolean; // Disable editing operations for share users
  onPatientFilesReady?: (refreshFunction: () => Promise<void>) => void; // Callback to provide refresh function to parent
  // NEW: Callback to provide file operation handlers to parent
  onFileOperationsReady?: (handlers: {
    handleFileDeleted: (fileId: string | number) => Promise<void>;
    handleFileRenamed: (file: FileModel, newName: string) => Promise<void>;
    handleFileDescriptionUpdated: (file: FileModel, description: string) => Promise<void>;
    handleFileTimestampsUpdated: (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => Promise<void>;
    handleFileTagsUpdated: (fileId: number, tagIds: number[]) => Promise<void>;
    handleFileDownloaded: (file: FileModel) => Promise<void>;
  }) => void;
  // Callback to provide tag data to parent component
  onTagDataReady?: (tagData: { allFlatTags: TagModel[]; allHierarchicalTags: TagModel[]; tagIdToTagMap: Map<number, TagModel>; isLoading: boolean }) => void;
  // Callback to provide worklist data to parent component
  onWorklistDataReady?: (worklistData: { selectedWorklist: WorklistViewModel | null; allWorklists: WorklistViewModel[]; currentPath: string }) => void;
  // Callback to provide form submissions data to parent component
  onFormSubmissionsReady?: (formData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formSubmissions: any[];
    areFormSubmissionsLoading: boolean;
    handleFormSubmissionsUpdated: () => Promise<void>;
  }) => void;
  // Optional location ID to use for worklist queries (e.g., from patient data in share mode)
  locationId?: number;
  // Enable upload mode automatically (e.g., when accessed via QR upload link)
  initialUploadMode?: boolean;
  // External control of sidebar state (for share mode)
  externalSidebarExpanded?: boolean;
  onExternalSidebarChange?: (expanded: boolean) => void;
  // NEW: Initial tag IDs for upload (extracted from share token message)
  initialTagIds?: number[] | null;
  // NEW: Disable internal data fetching (for upload-only mode)
  disableDataFetching?: boolean;
}

type CaseOption = { name: string; value: number | null };
const ALL_FILES_CASE: CaseOption = { name: 'All Files', value: null };
const ALL_FILES_WORKLIST: WorklistViewModel = {
  id: -1,
  name: 'All Files',
  entity: 'PatientFile',
  ordinal: -1,
  parentViewId: null,
  oDataResourceString: null,
  dataState: null,
  columnState: null,
  user: '',
};

const DEFAULT_SORT_CRITERIA: SortCriteria = 'Newest';
const DEFAULT_NUM_COLUMNS = 4;
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 8; // Set a reasonable max

// MOBILE_BREAKPOINT now imported from worklist-visibility-utils
const TOP_NAV_HEIGHT = '60px'; // Estimate height of elements above this component
const HEADER_HEIGHT = '60px'; // Estimate height of PatientFilesHeader
const GALLERY_SCROLL_SESSION_KEY = 'patientFilesGalleryScrollPos'; // Key for session storage

// Use slugify and getTagPath from worklistNavigationService

// Move mapping here or import from a shared location
const CATEGORY_TO_DEFAULT_TAG_NAMES: Record<number, string[]> = {
  [DocumentCategories.FINAL_REPORT.value]: ['report', 'document', 'final report'],
  [DocumentCategories.EXAM.value]: ['image', 'radiology', 'exam'],
  [DocumentCategories.Pathology.value]: ['pathology', 'slide', 'microscopy', 'report'],
  [DocumentCategories.EKG.value]: ['ekg', 'cardiology'],
  [DocumentCategories.DOCUMENT.value]: ['document'],
  [DocumentCategories.CT.value]: ['image', 'radiology', 'ct'],
  [DocumentCategories.ChestXRay.value]: ['image', 'radiology', 'x-ray', 'chest x-ray'],
  // Add/adjust mappings for other categories as needed
};

// Removed duplicate getAllDescendantTagIds - using centralized version from tagHelperUtils

// Removed all duplicate tag utility functions - using centralized versions from tagHelperUtils

const PatientFilesComponent: React.FC<PatientFilesProps> = ({
  patientId,
  gridData = [],
  onViewFile,
  className,
  onUploadModeChange,
  shareContext,
  isReadOnly = false,
  onPatientFilesReady,
  onFileOperationsReady,
  onTagDataReady,
  onWorklistDataReady,
  onFormSubmissionsReady,
  locationId,
  initialUploadMode = false,
  externalSidebarExpanded,
  onExternalSidebarChange,
  initialTagIds,
  disableDataFetching,
}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { '*': hierarchicalPath = '' } = useParams();
  // Mobile detection using consolidated utility
  const { isMobile, setIsMobile } = useMobileDetection();
  const theme = useTheme();
  const scrollableContentRef = useRef<HTMLDivElement>(null); // Ref for the scrollable area
  const { activeScheme } = useAuthentication();
  const { sessionLocation } = useSessionLocation();

  // Use file viewer context to share data with external viewers
  const fileViewerContext = useFileViewerContext();

  console.log('PatientFilesComponent', patientId, {
    gridDataLength: gridData?.length,
    hierarchicalPath,
    shareContext: shareContext?.shareLinkId,
    renderTime: new Date().toISOString(),
  });

  // Helper to get correct base path for files based on context
  const getFilesBasePath = useCallback(() => {
    if (shareContext?.isShareMode) {
      return `/share/${shareContext.shareLinkId}/files`;
    }
    return PatientRoutes.getFiles(patientId);
  }, [shareContext, patientId]);

  // Example usage of the new exam URL service:
  // To get a primary exam URL: FileUrlService.getExamUrl(examId, 'primary')
  // To get a secondary exam URL: FileUrlService.getExamUrl(examId, 'secondary')
  // This generates a tokenized URL for viewing exam files directly

  // Helper to get path for navigation within current context
  const getFilesPath = useCallback(
    (path?: string) => {
      if (shareContext?.isShareMode) {
        // For share mode, use relative paths
        return path || '';
      }
      // For patient edit mode, use absolute paths to ensure correct navigation
      const basePath = PatientRoutes.getFiles(patientId);
      return path ? `${basePath}/${path}` : basePath;
    },
    [shareContext, patientId],
  );

  // Helper to navigate within current context
  const navigateToPath = useCallback(
    (path: string) => {
      if (shareContext?.isShareMode) {
        // Use relative navigation for share mode
        navigate(path, { relative: 'route' });
      } else {
        // Use absolute navigation for patient edit mode
        navigate(path);
      }
    },
    [navigate, shareContext],
  );

  // --- State Management --- //
  const [numberOfColumns, setNumberOfColumns] = useState<number>(DEFAULT_NUM_COLUMNS);
  const [filterTagIds, setFilterTagIds] = useState<number[] | null>(null);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean | null>(null); // null = not initialized

  // Use external sidebar control when provided (for share mode)
  const effectiveSidebarExpanded = externalSidebarExpanded !== undefined ? externalSidebarExpanded : isSidebarExpanded;
  const handleSidebarChange = useCallback(
    (expanded: boolean) => {
      if (onExternalSidebarChange) {
        onExternalSidebarChange(expanded);
      } else {
        setIsSidebarExpanded(expanded);
      }
    },
    [onExternalSidebarChange],
  );
  const [sortCriteria, setSortCriteria] = useState<SortCriteria>(DEFAULT_SORT_CRITERIA);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(ALL_FILES_CASE.value);
  const [selectedFileForPreview, setSelectedFileForPreview] = useState<FileModel | null>(null);
  // Remove viewMode - always use smart stacking logic
  // const [viewMode, setViewMode] = useState<ViewMode>('stacked');
  const [allCounts, setAllCounts] = useState<Record<string, number>>({});
  const [isUploadMode, setIsUploadMode] = useState(initialUploadMode);
  // Add data update indicator state
  const [hasDataUpdates, setHasDataUpdates] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);

  // Note: Form submissions query will be initialized after preparedFiles is available

  // Upload mode handlers - declared early to avoid hoisting issues
  const handleExitUploadMode = useCallback(() => {
    setIsUploadMode(false);
    onUploadModeChange?.(false); // Notify parent component
    // No navigation needed - we stay in the same view/folder context
  }, [onUploadModeChange]);

  // Notify parent of initial upload mode state and handle direct upload access
  useEffect(() => {
    if (initialUploadMode) {
      onUploadModeChange?.(true);
      // For direct upload access, we might want to clear any existing preview
      setSelectedFileForPreview(null);
    }
  }, [initialUploadMode, onUploadModeChange]);

  // Form methods for upload mode
  const uploadFormMethods = useForm();

  // QR Code modal state for upload mode
  const [isQrModalVisible, setIsQrModalVisible] = useState(false);
  const [qrLinkId, setQrLinkId] = useState<string>('');
  const [isGeneratingQrCode, setIsGeneratingQrCode] = useState(false);

  // --- Data Fetching Hooks --- //
  const { data: cases, isLoading: isLoadingCases } = useCasesQuery(patientId);
  const { patient, files: rawFiles, preparedFiles, isLoading: isLoadingPatientFiles, fetchData } = usePatientFileData(patientId, gridData, disableDataFetching);

  // Form submissions using TanStack Query for caching and deduplication
  const { formSubmissions, isLoading: areFormSubmissionsLoading } = useFormSubmissionsQuery(preparedFiles || []);

  // Provide refresh function to parent component (e.g., ShareHome)
  useEffect(() => {
    if (onPatientFilesReady) {
      onPatientFilesReady(fetchData);
    }
  }, [onPatientFilesReady, fetchData]);
  // Get tags data for file operations using typed API query
  const { data: rawTags = [], isLoading: isLoadingTags } = useApiQuery({
    queryKey: ['tags', 'levels'],
    apiFn: apiClient.tagsClient.getLevelTags,
    params: [],
  });

  // Process tags data using centralized utility
  const {
    allHierarchicalTags: fetchedHierarchicalTags,
    allFlatTags: fetchedFlatTags,
    tagNameToIdMap: fetchedTagNameToIdMap,
    tagIdToTagMap: fetchedTagIdToTagMap,
  } = useMemo(() => {
    return processTagData(rawTags);
  }, [rawTags]);

  // Provide tag data to parent component when ready
  useEffect(() => {
    if (onTagDataReady && !isLoadingTags) {
      onTagDataReady({
        allFlatTags: fetchedFlatTags,
        allHierarchicalTags: fetchedHierarchicalTags,
        tagIdToTagMap: fetchedTagIdToTagMap,
        isLoading: isLoadingTags,
      });
    }
  }, [onTagDataReady, fetchedFlatTags, fetchedHierarchicalTags, fetchedTagIdToTagMap, isLoadingTags]);

  // Form submissions refresh handler using TanStack Query
  const handleFormSubmissionsUpdated = useCallback(async () => {
    if (!preparedFiles || preparedFiles.length === 0) return;
    const fileIds = preparedFiles.map((f) => f.id).sort();
    await queryClient.invalidateQueries({ queryKey: ['formSubmissions', fileIds] });
  }, [queryClient, preparedFiles]);

  // Provide form submissions data to parent component when ready
  useEffect(() => {
    if (onFormSubmissionsReady) {
      onFormSubmissionsReady({
        formSubmissions: formSubmissions || [],
        areFormSubmissionsLoading,
        handleFormSubmissionsUpdated,
      });
    }
  }, [onFormSubmissionsReady, formSubmissions, areFormSubmissionsLoading, handleFormSubmissionsUpdated]);

  // Removed stable wrappers as they're no longer needed for useMergedTags

  const { mergedHierarchicalTags, mergedFlatTags, mergedTagNameToIdMap, mergedTagIdToTagMap, isLoadingMergedTags } = useMergedTags({
    fetchedHierarchicalTags,
    fetchedTagNameToIdMap,
    fetchedTagIdToTagMap,
    isLoadingTags,
  });

  // --- URL-Driven State: Hierarchical Path Parsing ---
  const parseHierarchicalPath = useMemo(() => {
    if (!hierarchicalPath || !mergedHierarchicalTags || mergedHierarchicalTags.length === 0) {
      return { tagIds: null, isValidPath: true };
    }

    // Handle file ID paths (numeric segments)
    if (/^\d+$/.test(hierarchicalPath)) {
      // This is a file ID, not a tag path
      return { tagIds: null, isValidPath: true, fileId: parseInt(hierarchicalPath, 10) };
    }

    // Parse hierarchical tag path like "imaging/ct"
    const pathSegments = hierarchicalPath.split('/').filter((segment) => segment.length > 0);
    if (pathSegments.length === 0) {
      return { tagIds: null, isValidPath: true };
    }

    // Create a map of slug to tag for efficient lookup
    const slugToTagMap = new Map<string, TagModel>();
    const processTagsRecursively = (tags: TagModel[]) => {
      tags.forEach((tag) => {
        const slug = slugify(tag.name);
        slugToTagMap.set(slug, tag);
        if (tag.children) {
          processTagsRecursively(tag.children);
        }
      });
    };
    processTagsRecursively(mergedHierarchicalTags);

    // Find the deepest matching tag
    let currentTag: TagModel | undefined;
    let validSegments = 0;

    for (const segment of pathSegments) {
      const tag = slugToTagMap.get(segment);
      if (tag) {
        // Verify this tag is a child of the current tag (or root level)
        if (!currentTag) {
          // First segment - must be a root level tag
          const isRootTag = mergedHierarchicalTags.some((rootTag) => rootTag.id === tag.id);
          if (isRootTag) {
            currentTag = tag;
            validSegments++;
          } else {
            break; // Invalid path
          }
        } else {
          // Subsequent segments - must be children of current tag
          const isChildTag = currentTag.children?.some((child) => child.id === tag.id);
          if (isChildTag) {
            currentTag = tag;
            validSegments++;
          } else {
            break; // Invalid path
          }
        }
      } else {
        break; // Invalid segment
      }
    }

    if (currentTag && validSegments === pathSegments.length) {
      // Get all descendant tag IDs for filtering
      const tagIds = getAllDescendantTagIds(currentTag.id, mergedHierarchicalTags);
      return { tagIds, isValidPath: true, currentTag };
    }

    return { tagIds: null, isValidPath: false };
  }, [hierarchicalPath, mergedHierarchicalTags]);

  // --- New section for worklist data using generic API query --- //
  // Use provided locationId (e.g., from patient data in share mode) or fall back to session location
  const effectiveLocationId = locationId ?? sessionLocation?.id ?? 0;
  const {
    data: worklistsData,
    isLoading: isLoadingWorklists,
    error: worklistsError,
    refetch: refetchWorklists,
  } = useApiQuery({
    queryKey: ['worklist-views', false, effectiveLocationId, 'PatientFile'],
    apiFn: apiClient.workListViewClient.getViews,
    params: [false, effectiveLocationId, 'PatientFile'],
    skipIfInvalidParams: false, // Allow locationId to be 0
    debug: true, // Enable debug to see the auth mode auto-detection
  });

  // Worklist query state tracking (removed logging to reduce console noise)

  // Process worklists data when it changes
  const allWorklists = useMemo(() => {
    if (!worklistsData || worklistsData.length === 0) {
      console.log('[PatientFiles] No worklist data, using default ALL_FILES_WORKLIST');
      return [ALL_FILES_WORKLIST]; // Always include All Files worklist
    }

    // Sort worklists and prepend the All Files worklist
    const sorted = [...worklistsData].sort((a, b) => {
      // Handle null ordinals: sort them towards the end
      if (a.ordinal === null && b.ordinal === null) {
        return a.name.localeCompare(b.name); // Secondary sort by name
      }
      if (a.ordinal === null) return 1; // a comes after b
      if (b.ordinal === null) return -1; // b comes after a
      if (a.ordinal !== b.ordinal) {
        return a.ordinal - b.ordinal; // Primary sort by ordinal
      }
      return a.name.localeCompare(b.name); // Secondary sort by name
    });

    // Prepend the All Files worklist
    return [ALL_FILES_WORKLIST, ...sorted];
  }, [worklistsData]);

  // Use simplified worklist slugs based on names
  const worklistToSlugMap = useMemo(() => {
    const map = new Map<number, string>();
    if (!allWorklists) return map;

    allWorklists.forEach((worklist) => {
      map.set(worklist.id, slugify(worklist.name));
    });
    return map;
  }, [allWorklists]);

  const slugToWorklistIdMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const [id, slug] of worklistToSlugMap.entries()) {
      map.set(slug, id);
    }
    return map;
  }, [worklistToSlugMap]);

  // --- Derived Data Hooks --- //
  const { finalFilteredAndSortedFiles } = useFileFiltering(
    preparedFiles,
    filterTagIds,
    sortCriteria,
    mergedHierarchicalTags,
    mergedTagNameToIdMap,
    isLoadingMergedTags,
  );
  const { displayableTags } = useTagSidebarData(
    preparedFiles,
    finalFilteredAndSortedFiles,
    mergedHierarchicalTags,
    mergedFlatTags,
    mergedTagNameToIdMap,
    isLoadingMergedTags,
  );

  // Determine the initial tags for the upload modal - Ensure this is the ONLY declaration
  const initialTagsForUpload = useMemo(() => {
    if (filterTagIds && filterTagIds.length > 0 && mergedTagIdToTagMap.size > 0) {
      return filterTagIds.map((id) => mergedTagIdToTagMap.get(id)).filter((tag): tag is TagModel => tag !== undefined);
    }
    return null;
  }, [filterTagIds, mergedTagIdToTagMap]);

  // Fallback for single initialTag - Ensure this is the ONLY declaration
  const singleInitialTagForUpload = initialTagsForUpload && initialTagsForUpload.length > 0 ? initialTagsForUpload[0] : null;

  // Mobile optimizations for better iPhone 13 performance
  const mobileOptimizations = useMobileOptimizations({
    isMobile,
    isViewingFiles: true,
    enableVirtualization: true,
  });

  // Handle worklist errors
  useEffect(() => {
    if (worklistsError) {
      console.error('Error fetching worklist views:', worklistsError);
      NotificationsService.displayError('Failed to load worklists.');
    }
  }, [worklistsError]);

  // Effect to calculate worklist counts locally (including All Files)
  useEffect(() => {
    if (!allWorklists || !preparedFiles || isLoadingMergedTags || !mergedHierarchicalTags || mergedHierarchicalTags.length === 0) {
      return;
    }

    const newCounts: Record<string, number> = {};

    allWorklists.forEach((worklist) => {
      // Handle All Files worklist
      if (worklist.id === ALL_FILES_WORKLIST.id) {
        newCounts[worklist.id.toString()] = preparedFiles.length;
        return;
      }

      if (worklist.oDataResourceString) {
        const tagIdMatch = /TagId~in~([\d,]+)/.exec(worklist.oDataResourceString);
        if (tagIdMatch && typeof tagIdMatch[1] === 'string') {
          const rawTagIdsString = tagIdMatch[1];
          const worklistTagIds = rawTagIdsString
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .map(Number)
            .filter((id) => !isNaN(id));

          if (worklistTagIds.length > 0) {
            // Get all effective tag IDs for the worklist (including descendants)
            const effectiveWorklistTagIds = new Set<number>();
            worklistTagIds.forEach((tagId) => {
              getAllDescendantTagIds(tagId, mergedHierarchicalTags).forEach((id) => effectiveWorklistTagIds.add(id));
            });

            let count = 0;
            preparedFiles.forEach((file) => {
              if (file.tagIds?.some((fileTagId) => effectiveWorklistTagIds.has(fileTagId))) {
                count++;
              }
            });
            newCounts[worklist.id.toString()] = count;
          } else {
            newCounts[worklist.id.toString()] = preparedFiles.length;
          }
        } else {
          newCounts[worklist.id.toString()] = preparedFiles.length;
        }
      } else {
        newCounts[worklist.id.toString()] = preparedFiles.length;
      }
    });
    setAllCounts(newCounts);
  }, [allWorklists, preparedFiles, mergedHierarchicalTags, isLoadingMergedTags]);

  // Effect to initialize sidebar state based on mobile and worklist count
  useEffect(() => {
    // Only initialize if not already set and we have the necessary data
    if (isSidebarExpanded !== null || !allWorklists) {
      return;
    }

    // Use consolidated worklist visibility logic
    const { shouldExpand, reason } = determineWorklistSidebarState(isMobile, allWorklists, true);

    console.log('[PatientFiles] Initializing sidebar state:', {
      reason,
      isMobile,
      worklistCount: allWorklists.length,
      willExpandSidebar: shouldExpand,
    });

    handleSidebarChange(shouldExpand);
  }, [isSidebarExpanded, allWorklists, isMobile]);

  const handleWorklistRefresh = useCallback(() => {
    refetchWorklists();
  }, [refetchWorklists]);

  // --- URL-Driven State Management ---
  const selectedWorklist = useMemo(() => {
    if (!hierarchicalPath || !allWorklists || slugToWorklistIdMap.size === 0) return null;
    const worklistId = slugToWorklistIdMap.get(hierarchicalPath);
    return allWorklists.find((w) => w.id === worklistId) || null;
  }, [hierarchicalPath, allWorklists, slugToWorklistIdMap]);
  const selectedWorklistId = selectedWorklist?.id ?? null;

  // Compute initial tag IDs for upload, considering share token, filter, and worklist context
  const initialTagIdsForUpload = useMemo(() => {
    console.log('[PatientFiles] Computing initialTagIdsForUpload:', {
      initialTagIds,
      filterTagIds,
      selectedWorklist: selectedWorklist?.name,
      isLoadingMergedTags,
      mergedTagIdToTagMapSize: mergedTagIdToTagMap.size,
    });

    // PRIORITY 1: If we have initial tag IDs from share token (QR codes), use them
    if (initialTagIds && initialTagIds.length > 0) {
      console.log('[PatientFiles] Using initial tag IDs from share token:', initialTagIds);

      // ADDED: Validate that the tag IDs exist in our tag map (only if tags are loaded)
      if (!isLoadingMergedTags && mergedTagIdToTagMap.size > 0) {
        const validTagIds = initialTagIds.filter((id) => mergedTagIdToTagMap.has(id));
        const invalidTagIds = initialTagIds.filter((id) => !mergedTagIdToTagMap.has(id));

        if (invalidTagIds.length > 0) {
          console.warn('[PatientFiles] Some initial tag IDs are invalid:', invalidTagIds);
        }

        if (validTagIds.length > 0) {
          console.log('[PatientFiles] Using validated initial tag IDs:', validTagIds);
          return validTagIds;
        } else {
          console.warn('[PatientFiles] No valid initial tag IDs found, falling back to other priorities');
        }
      } else {
        // Tags are still loading, return the original IDs and let the Upload component handle validation
        console.log('[PatientFiles] Tags still loading, returning unvalidated initial tag IDs');
        return initialTagIds;
      }
    }

    // PRIORITY 2: If we have filter tag IDs, use them
    if (filterTagIds && filterTagIds.length > 0) {
      console.log('[PatientFiles] Using filter tag IDs:', filterTagIds);
      return filterTagIds;
    }

    // PRIORITY 3: If we're in a specific worklist (not All Files), extract its tag IDs for upload
    if (selectedWorklist && selectedWorklist.id !== ALL_FILES_WORKLIST.id && selectedWorklist.oDataResourceString) {
      const tagIdMatch = /TagId~in~([\d,]+)/.exec(selectedWorklist.oDataResourceString);
      if (tagIdMatch && typeof tagIdMatch[1] === 'string') {
        const rawTagIdsString = tagIdMatch[1];
        const tagIds = rawTagIdsString
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
          .map(Number)
          .filter((id) => !isNaN(id));

        // Use the parent worklist's direct tag IDs for upload (not descendants)
        if (tagIds.length > 0) {
          console.log('[PatientFiles] Using worklist tag IDs for upload:', tagIds);
          return tagIds;
        }
      }
    }

    console.log('[PatientFiles] No initial tag IDs found, returning empty array');
    return [];
  }, [initialTagIds, filterTagIds, selectedWorklist, isLoadingMergedTags, mergedTagIdToTagMap]);

  // Determine if we're viewing a specific tag (not a worklist) - moved after selectedWorklist definition
  const isViewingSpecificTag = useMemo(() => {
    return Boolean(parseHierarchicalPath.isValidPath && parseHierarchicalPath.tagIds && !selectedWorklist);
  }, [parseHierarchicalPath, selectedWorklist]);

  const { groupedFiles: groupedFilesForStackedView, groupMetadata } = useFileGrouping(
    finalFilteredAndSortedFiles,
    selectedWorklist,
    allWorklists,
    mergedHierarchicalTags,
    mergedTagNameToIdMap,
  );

  // Effect to apply filter based on hierarchical path or worklist (updated for All Files)
  useEffect(() => {
    console.log('[PatientFiles] Applying filter for hierarchicalPath:', hierarchicalPath);

    // Handle All Files worklist
    if (selectedWorklist?.id === ALL_FILES_WORKLIST.id) {
      setFilterTagIds(null);
      console.log('[PatientFiles] All Files worklist selected, clearing tag filter.');
      setSelectedFileForPreview(null);
      return;
    }

    // First try worklist-based filtering to maintain worklist context
    if (selectedWorklist?.oDataResourceString) {
      const tagIdMatch = /TagId~in~([\d,]+)/.exec(selectedWorklist.oDataResourceString);
      if (tagIdMatch && typeof tagIdMatch[1] === 'string') {
        const rawTagIdsString = tagIdMatch[1];
        const tagIds = rawTagIdsString
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
          .map(Number)
          .filter((id) => !isNaN(id));

        // For parent worklists, include all descendant tags to show grouped view
        const effectiveTagIds = new Set<number>();
        tagIds.forEach((tagId) => {
          getAllDescendantTagIds(tagId, mergedHierarchicalTags || []).forEach((id) => effectiveTagIds.add(id));
        });

        setFilterTagIds(Array.from(effectiveTagIds));
        console.log('[PatientFiles] Set filter tag IDs from worklist (including descendants):', Array.from(effectiveTagIds));
      } else {
        setFilterTagIds(null);
        console.log('[PatientFiles] Worklist has no TagId filter, clearing tag filter.');
      }
    } else {
      // Fallback to hierarchical path parsing when no worklist is selected
      // Parse the path directly here to avoid circular dependencies
      if (hierarchicalPath && mergedHierarchicalTags && mergedHierarchicalTags.length > 0) {
        // Handle file ID paths (numeric segments)
        if (!/^\d+$/.test(hierarchicalPath)) {
          // Parse hierarchical tag path like "imaging/ct"
          const pathSegments = hierarchicalPath.split('/').filter((segment) => segment.length > 0);
          if (pathSegments.length > 0) {
            // Create a map of slug to tag for efficient lookup
            const slugToTagMap = new Map<string, TagModel>();
            const processTagsRecursively = (tags: TagModel[]) => {
              tags.forEach((tag) => {
                const slug = slugify(tag.name);
                slugToTagMap.set(slug, tag);
                if (tag.children) {
                  processTagsRecursively(tag.children);
                }
              });
            };
            processTagsRecursively(mergedHierarchicalTags);

            // Find the deepest matching tag
            let currentTag: TagModel | undefined;
            let validSegments = 0;

            for (const segment of pathSegments) {
              const tag = slugToTagMap.get(segment);
              if (tag) {
                // Verify this tag is a child of the current tag (or root level)
                if (!currentTag) {
                  // First segment - must be a root level tag
                  const isRootTag = mergedHierarchicalTags.some((rootTag) => rootTag.id === tag.id);
                  if (isRootTag) {
                    currentTag = tag;
                    validSegments++;
                  } else {
                    break; // Invalid path
                  }
                } else {
                  // Subsequent segments - must be children of current tag
                  const isChildTag = currentTag.children?.some((child) => child.id === tag.id);
                  if (isChildTag) {
                    currentTag = tag;
                    validSegments++;
                  } else {
                    break; // Invalid path
                  }
                }
              } else {
                break; // Invalid segment
              }
            }

            if (currentTag && validSegments === pathSegments.length) {
              // Get all descendant tag IDs for filtering
              const tagIds = getAllDescendantTagIds(currentTag.id, mergedHierarchicalTags);
              setFilterTagIds(tagIds);
              console.log('[PatientFiles] Set filter tag IDs from hierarchical path:', tagIds);
            } else {
              // Only clear filter if it's not already null to prevent infinite loops
              setFilterTagIds((prev) => {
                if (prev !== null) {
                  console.log('[PatientFiles] Invalid hierarchical path, clearing tag filter.');
                  return null;
                }
                return prev;
              });
            }
          } else {
            setFilterTagIds((prev) => {
              if (prev !== null) {
                console.log('[PatientFiles] Empty path segments, clearing tag filter.');
                return null;
              }
              return prev;
            });
          }
        } else {
          setFilterTagIds((prev) => {
            if (prev !== null) {
              console.log('[PatientFiles] File ID path detected, clearing tag filter.');
              return null;
            }
            return prev;
          });
        }
      } else {
        setFilterTagIds((prev) => {
          if (prev !== null) {
            console.log('[PatientFiles] No valid worklist or path, clearing tag filter.');
            return null;
          }
          return prev;
        });
      }
    }
    setSelectedFileForPreview(null); // Reset preview when filter changes
  }, [hierarchicalPath, selectedWorklist, mergedHierarchicalTags]);

  // Enhanced handler for hierarchical tag navigation that preserves worklist context
  const handleTagNavigation = useCallback(
    (tagId: number | null) => {
      console.log('[PatientFiles] handleTagNavigation called:', {
        tagId,
        currentPath: hierarchicalPath,
        selectedWorklist: selectedWorklist?.name,
        isUploadMode,
        hasFilterTagIds: !!filterTagIds,
        filterTagIds,
      });

      // Check if we're in upload mode and warn user
      if (isUploadMode) {
        const shouldContinue = window.confirm('You have an upload in progress. Are you sure you want to navigate away? Any unsaved uploads will be lost.');
        if (!shouldContinue) {
          return;
        }
        // Exit upload mode if user confirms
        handleExitUploadMode();
      }

      if (tagId === null) {
        // Navigate to root (all files) - try to preserve current worklist if any
        if (selectedWorklist) {
          const worklistSlug = worklistToSlugMap.get(selectedWorklist.id);
          if (worklistSlug) {
            console.log('[PatientFiles] Navigating to worklist root:', worklistSlug);
            navigateToPath(getFilesPath(worklistSlug));
            return;
          }
        }
        console.log('[PatientFiles] Navigating to files root');
        navigateToPath(getFilesPath());
      } else {
        // If we're in a worklist context, try to maintain it
        if (selectedWorklist) {
          const worklistSlug = worklistToSlugMap.get(selectedWorklist.id);
          if (worklistSlug) {
            // Create a combined path that preserves worklist context
            const flatTagsMap = new Map(mergedFlatTags?.map((t) => [t.id, t]) || []);
            const tagPath = getTagPath(tagId, flatTagsMap);
            const fullPath = getFilesPath(`${worklistSlug}/${tagPath}`);
            console.log('[PatientFiles] Navigating to tag within worklist:', { worklistSlug, tagPath, fullPath });
            navigateToPath(fullPath);
            return;
          }
        }

        // Fallback to regular tag navigation
        const flatTagsMap = new Map(mergedFlatTags?.map((t) => [t.id, t]) || []);
        const tagPath = getTagPath(tagId, flatTagsMap);
        const fullPath = getFilesPath(tagPath);
        console.log('[PatientFiles] Navigating to tag (no worklist context):', { tagPath, fullPath });
        navigateToPath(fullPath);
      }
    },
    [navigateToPath, getFilesPath, mergedFlatTags, selectedWorklist, worklistToSlugMap, hierarchicalPath, isUploadMode, filterTagIds, handleExitUploadMode],
  );

  // Enhanced handler for worklist navigation with upload mode awareness
  const handleSelectedWorklistIdChange = useCallback(
    (worklistId: number | null) => {
      console.log('[PatientFiles] handleSelectedWorklistIdChange called:', {
        worklistId,
        currentWorklistId: selectedWorklistId,
        isUploadMode,
        currentPath: hierarchicalPath,
        allWorklistsCount: allWorklists?.length || 0,
      });

      if (worklistId === selectedWorklistId) {
        console.log('[PatientFiles] Same worklist selected, no navigation needed');
        return; // No change needed
      }

      // If in upload mode, warn about navigation
      if (isUploadMode) {
        console.warn('[PatientFiles] User attempting to navigate while in upload mode:', {
          fromWorklist: selectedWorklist?.name,
          toWorklistId: worklistId,
          shouldPreventNavigation: true,
        });

        const shouldContinue = window.confirm('You have an upload in progress. Are you sure you want to navigate away? Any unsaved uploads will be lost.');
        if (!shouldContinue) {
          return;
        }
        // Exit upload mode if user confirms
        handleExitUploadMode();
      }

      if (worklistId === null || worklistId === ALL_FILES_WORKLIST.id) {
        console.log('[PatientFiles] Navigating to All Files view');
        navigateToPath(getFilesPath());
      } else {
        const targetWorklist = allWorklists?.find((w) => w.id === worklistId);
        if (targetWorklist) {
          const worklistSlug = worklistToSlugMap.get(worklistId);
          if (worklistSlug) {
            const fullPath = getFilesPath(worklistSlug);
            console.log('[PatientFiles] Navigating to specific worklist:', {
              worklistName: targetWorklist.name,
              worklistSlug,
              fullPath,
            });
            navigateToPath(fullPath);
          } else {
            console.error('[PatientFiles] Could not find slug for worklist:', targetWorklist);
            navigateToPath(getFilesPath()); // Fallback to all files
          }
        } else {
          console.error('[PatientFiles] Could not find worklist with ID:', worklistId);
          navigateToPath(getFilesPath()); // Fallback to all files
        }
      }
    },
    [
      selectedWorklistId,
      isUploadMode,
      selectedWorklist,
      hierarchicalPath,
      allWorklists,
      navigateToPath,
      getFilesPath,
      patientId,
      worklistToSlugMap,
      handleExitUploadMode,
    ],
  );

  const handleCreateWorklist = useCallback(
    async (worklist: WorklistViewModel) => {
      try {
        const newWorklist = await apiClient.workListViewClient.createView(worklist);
        NotificationsService.displaySuccess('Worklist created successfully.');
        await refetchWorklists(); // Refresh the list
      } catch (error) {
        console.error('Error creating worklist:', error);
        NotificationsService.displayError('Failed to create worklist.');
      }
    },
    [apiClient.workListViewClient, refetchWorklists],
  );

  const handleUpdateWorklist = useCallback(
    async (worklists: WorklistViewModel[]) => {
      try {
        for (const worklist of worklists) {
          await apiClient.workListViewClient.updateView(worklist);
        }
        NotificationsService.displaySuccess('Worklist updated successfully.');
        await refetchWorklists(); // Refresh the list
      } catch (error) {
        console.error('Error updating worklist:', error);
        NotificationsService.displayError('Failed to update worklist.');
      }
    },
    [apiClient.workListViewClient, refetchWorklists],
  );

  const handleDeleteWorklist = useCallback(
    async (worklist: WorklistViewModel) => {
      try {
        await apiClient.workListViewClient.deleteView(worklist.id);
        NotificationsService.displaySuccess('Worklist deleted successfully.');
        // After deletion, navigate to the base files view if the deleted one was selected
        if (selectedWorklistId === worklist.id) {
          const basePath = getFilesPath();
          if (shareContext?.isShareMode) {
            navigate(basePath, { replace: true, relative: 'route' });
          } else {
            navigate(basePath, { replace: true });
          }
        }
        await refetchWorklists(); // Refresh the list
      } catch (error) {
        console.error('Error deleting worklist:', error);
        NotificationsService.displayError('Failed to delete worklist.');
      }
    },
    [apiClient.workListViewClient, refetchWorklists, selectedWorklistId, getFilesPath, navigate, shareContext],
  );

  // --- Effects --- //
  // Mobile detection is now handled by useMobileDetection hook

  // Note: Worklist fetching is now handled automatically by TanStack Query
  // No manual fetch needed since useApiQuery handles authentication state

  // Update context with current worklist and path information
  useEffect(() => {
    fileViewerContext.setSelectedWorklist(selectedWorklist);
    fileViewerContext.setAllWorklists(allWorklists);
    fileViewerContext.setCurrentPath(hierarchicalPath);
  }, [selectedWorklist, allWorklists, hierarchicalPath, fileViewerContext]);

  // Provide worklist data to parent component when ready
  useEffect(() => {
    if (onWorklistDataReady && allWorklists && allWorklists.length > 0) {
      onWorklistDataReady({
        selectedWorklist,
        allWorklists,
        currentPath: hierarchicalPath,
      });
    }
  }, [onWorklistDataReady, selectedWorklist, allWorklists, hierarchicalPath]);

  // Effect to select default worklist if none is selected in URL
  useEffect(() => {
    // Only run if worklists are loaded, and there's no path in the URL
    if (allWorklists?.length && !hierarchicalPath && slugToWorklistIdMap.size > 0) {
      const defaultWorklist = allWorklists.find((wl) => wl.ordinal !== null) || allWorklists[0];
      if (defaultWorklist) {
        console.log('[PatientFiles] Auto-selecting default worklist and updating URL:', defaultWorklist);
        const defaultSlug = worklistToSlugMap.get(defaultWorklist.id);
        if (defaultSlug) {
          const fullPath = getFilesPath(defaultSlug);
          navigateToPath(fullPath);
        }
      }
    }
  }, [allWorklists, hierarchicalPath, navigateToPath, getFilesPath, worklistToSlugMap, slugToWorklistIdMap]);

  const totalLoading = isLoadingPatientFiles || isLoadingMergedTags || isLoadingCases || isLoadingWorklists;

  // --- Handlers --- //

  // Preview Pane Handlers
  const handleFileSelectForPreview = useCallback((file: FileModel | null) => {
    setSelectedFileForPreview(file);
  }, []);

  const handleSaveScrollPosition = useCallback(() => {
    if (scrollableContentRef.current) {
      sessionStorage.setItem(GALLERY_SCROLL_SESSION_KEY, scrollableContentRef.current.scrollTop.toString());
    }
  }, []);

  // Centralized file operation handlers that can be used by parent components
  const handleFileDeleted = useCallback(
    async (fileId: string | number) => {
      try {
        const authMode = activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required';
        await apiClient.filesClient.deleteFile(Number(fileId), authMode);
        NotificationsService.displaySuccess('File deleted successfully.');
        await fetchData();
        // Clear preview if the deleted file was selected
        if (selectedFileForPreview?.id === Number(fileId)) {
          setSelectedFileForPreview(null);
        }
      } catch (error) {
        console.error('Delete file error:', error);
        NotificationsService.displayError('Failed to delete file.');
        throw error;
      }
    },
    [apiClient, fetchData, selectedFileForPreview?.id, activeScheme],
  );

  const handleFileRenamed = useCallback(
    async (file: FileModel, newName: string) => {
      if (!newName || newName === file.fileName) return;
      try {
        const patchDoc = [{ op: 'replace', path: '/fileName', value: newName }];
        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('File renamed successfully.');
        await fetchData();
        // Update preview if the renamed file was selected
        if (selectedFileForPreview?.id === file.id) {
          const updatedFile = preparedFiles.find((f) => f.id === file.id);
          setSelectedFileForPreview(updatedFile || null);
        }
      } catch (error) {
        console.error('Rename file error:', error);
        NotificationsService.displayError('Failed to rename file.');
        throw error;
      }
    },
    [apiClient, fetchData, selectedFileForPreview?.id, preparedFiles],
  );

  const handleFileDescriptionUpdated = useCallback(
    async (file: FileModel, description: string) => {
      try {
        const patchDoc = [{ op: 'replace', path: '/description', value: description }];
        await apiClient.filesClient.renameFile(file.id, patchDoc);
        NotificationsService.displaySuccess('Description updated successfully.');
        await fetchData();
        // Update preview if the updated file was selected
        if (selectedFileForPreview?.id === file.id) {
          const updatedFile = preparedFiles.find((f) => f.id === file.id);
          setSelectedFileForPreview(updatedFile || null);
        }
      } catch (error) {
        console.error('Update description error:', error);
        NotificationsService.displayError('Failed to update description.');
        throw error;
      }
    },
    [apiClient, fetchData, selectedFileForPreview?.id, preparedFiles],
  );

  const handleFileTimestampsUpdated = useCallback(
    async (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => {
      try {
        const patchDoc: { op: string; path: string; value: string | null }[] = [];

        if (captureTime !== undefined) {
          patchDoc.push({
            op: 'replace',
            path: '/captureTime',
            value: captureTime ? dayjs(captureTime).toISOString() : null,
          });
        }

        if (eventTime !== undefined) {
          patchDoc.push({
            op: 'replace',
            path: '/eventTime',
            value: eventTime ? dayjs(eventTime).toISOString() : null,
          });
        }

        if (patchDoc.length > 0) {
          await apiClient.filesClient.renameFile(file.id, patchDoc);
          NotificationsService.displaySuccess('Timestamps updated successfully.');
          await fetchData();
          // Update preview if the updated file was selected
          if (selectedFileForPreview?.id === file.id) {
            const updatedFile = preparedFiles.find((f) => f.id === file.id);
            setSelectedFileForPreview(updatedFile || null);
          }
        }
      } catch (error) {
        console.error('Update timestamps error:', error);
        NotificationsService.displayError('Failed to update timestamps.');
        throw error;
      }
    },
    [apiClient, fetchData, selectedFileForPreview?.id, preparedFiles],
  );

  const handleFileTagsUpdated = useCallback(
    async (fileId: number, tagIds: number[]) => {
      try {
        const authMode = activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required';
        await apiClient.filesClient.updateFileTags(fileId, tagIds, authMode);
        NotificationsService.displaySuccess('Tags updated successfully.');
        await fetchData();
        // Update preview if the updated file was selected
        if (selectedFileForPreview?.id === fileId) {
          const updatedFile = preparedFiles.find((f) => f.id === fileId);
          setSelectedFileForPreview(updatedFile || null);
        }
      } catch (error) {
        console.error('Update tags error:', error);
        NotificationsService.displayError('Failed to update tags.');
        throw error;
      }
    },
    [apiClient, fetchData, selectedFileForPreview?.id, preparedFiles, activeScheme],
  );

  const handleFileDownloaded = useCallback(
    async (file: FileModel) => {
      if (!file?.location) {
        NotificationsService.displayError('File location is missing, cannot generate download link.');
        return;
      }
      try {
        const locationParts = file.location.split('/');
        if (locationParts.length < 2) {
          NotificationsService.displayError('Invalid file location format.');
          console.error('Invalid file location format:', file.location);
          return;
        }
        const containerName = locationParts[0];
        const blobName = locationParts.slice(1).join('/');

        const downloadSasUrl = await apiClient.filesClient.generateAzureBlobDownloadSasUrl(containerName, blobName);

        if (downloadSasUrl) {
          const link = document.createElement('a');
          link.href = downloadSasUrl;
          link.setAttribute('download', file.fileName || `download_${file.id}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          NotificationsService.displayError('Could not get download URL.');
        }
      } catch (error) {
        NotificationsService.displayError('Failed to download file.');
        console.error('Download file error:', error);
      }
    },
    [apiClient.filesClient],
  );

  // Provide file operation handlers to parent component
  useEffect(() => {
    if (onFileOperationsReady) {
      onFileOperationsReady({
        handleFileDeleted,
        handleFileRenamed,
        handleFileDescriptionUpdated,
        handleFileTimestampsUpdated,
        handleFileTagsUpdated,
        handleFileDownloaded,
      });
    }
  }, [
    onFileOperationsReady,
    handleFileDeleted,
    handleFileRenamed,
    handleFileDescriptionUpdated,
    handleFileTimestampsUpdated,
    handleFileTagsUpdated,
    handleFileDownloaded,
  ]);

  const handleUpdateTags = useCallback(
    async (fileId: number, tagIds: number[]) => {
      return handleFileTagsUpdated(fileId, tagIds);
    },
    [handleFileTagsUpdated],
  );

  const handleDeleteFileFromPreview = useCallback(
    async (fileToDelete: FileModel) => {
      return handleFileDeleted(fileToDelete.id);
    },
    [handleFileDeleted],
  );

  const handleUpdateTimestamps = useCallback(
    async (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => {
      return handleFileTimestampsUpdated(file, captureTime, eventTime);
    },
    [handleFileTimestampsUpdated],
  );

  // Determine stack name for virtual folder display
  const getStackNameForFiles = useCallback(
    (files: PreparedFile[]) => {
      if (files.length <= 1) return undefined;

      const firstFile = files[0];
      if (!firstFile) return undefined;

      // PRIORITY 1: If we have a selected worklist, determine which child worklist view the user will navigate to
      if (selectedWorklist && allWorklists && mergedHierarchicalTags && mergedTagNameToIdMap) {
        // Get the parent worklist's tag IDs (same logic as useFileGrouping)
        const tagIdMatch = /TagId~in~([\d,]+)/.exec(selectedWorklist.oDataResourceString ?? '');
        if (tagIdMatch?.[1]) {
          const worklistTagIds = tagIdMatch[1]
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
            .map(Number)
            .filter((id) => !isNaN(id));

          if (worklistTagIds.length > 0) {
            // Find child worklists that are subsets of the current worklist (same logic as useFileGrouping)
            const childWorklists = allWorklists.filter((childWorklist) => {
              if (childWorklist.id === selectedWorklist.id) return false;

              const childTagMatch = /TagId~in~([\d,]+)/.exec(childWorklist.oDataResourceString ?? '');
              if (childTagMatch?.[1]) {
                const childTagIds = childTagMatch[1]
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0)
                  .map(Number)
                  .filter((id) => !isNaN(id));

                // Check if child worklist tags are descendants of parent worklist tags
                return childTagIds.some((childTagId) =>
                  worklistTagIds.some((parentTagId) => {
                    const descendants = getAllDescendantTagIds(parentTagId, mergedHierarchicalTags);
                    return descendants.includes(childTagId);
                  }),
                );
              }
              return false;
            });

            // For each child worklist, check if the files in this stack belong to it
            for (const childWorklist of childWorklists) {
              const childTagMatch = /TagId~in~([\d,]+)/.exec(childWorklist.oDataResourceString ?? '');
              if (childTagMatch?.[1]) {
                const childTagIds = childTagMatch[1]
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0)
                  .map(Number)
                  .filter((id) => !isNaN(id));

                // Get all descendant tags for this child worklist (same logic as useFileGrouping)
                const effectiveChildTagIds = new Set<number>();
                childTagIds.forEach((tagId) => {
                  getAllDescendantTagIds(tagId, mergedHierarchicalTags).forEach((id) => effectiveChildTagIds.add(id));
                });

                // Check if the files in this stack belong to this child worklist
                let filesMatchThisWorklist = 0;
                files.forEach((file) => {
                  const fileTagIds = new Set<number>();

                  // Add tag IDs from file.tagIds property (PreparedFile has this)
                  if (file.tagIds) {
                    file.tagIds.forEach((tagId) => fileTagIds.add(tagId));
                  }

                  // Add derived tag IDs if the file has derivedTagNames (PreparedFile specific)
                  if (file.derivedTagNames) {
                    file.derivedTagNames.forEach((name: string) => {
                      const derivedId = mergedTagNameToIdMap.get(name.toLowerCase());
                      if (derivedId !== undefined) {
                        fileTagIds.add(derivedId);
                      }
                    });
                  }

                  // Check if this file belongs to the child worklist
                  if (Array.from(fileTagIds).some((tagId) => effectiveChildTagIds.has(tagId))) {
                    filesMatchThisWorklist++;
                  }
                });

                // If all or most files in the stack belong to this child worklist, use its name
                if (filesMatchThisWorklist > 0 && filesMatchThisWorklist >= files.length * 0.5) {
                  return childWorklist.name;
                }
              }
            }

            // If no specific child worklist found, show the parent worklist with file count
            return `${selectedWorklist.name} (${files.length} files)`;
          }
        }
      }

      // PRIORITY 2: Fallback to tag-based naming if no worklist context
      if (firstFile.tags && firstFile.tags.length > 0 && mergedTagIdToTagMap.size > 0) {
        const primaryTag = mergedTagIdToTagMap.get(firstFile.tags[0].id);
        if (primaryTag) {
          return `${primaryTag.name} (${files.length} files)`;
        }
      }

      // PRIORITY 3: Final fallback - return undefined to show no label
      return undefined;
    },
    [selectedWorklist, allWorklists, mergedHierarchicalTags, mergedTagNameToIdMap, mergedTagIdToTagMap],
  );

  // Main View Handlers (Gallery/Stacked)
  const handleFileSelect = useCallback(
    (file: FileModel) => {
      handleSaveScrollPosition(); // Save scroll before navigating

      // Clear stack name when viewing individual files
      fileViewerContext.setStackName(undefined);

      // Always open the file viewer instead of showing sidebar preview
      onViewFile?.(file, finalFilteredAndSortedFiles);
    },
    [onViewFile, finalFilteredAndSortedFiles, handleSaveScrollPosition, fileViewerContext],
  );

  // Enhanced file view handler that preserves worklist context in URL
  const handleViewFile = useCallback(
    (file: FileModel) => {
      if (onViewFile) {
        // Save current worklist context for the file viewer
        if (selectedWorklist) {
          fileViewerContext.setSelectedWorklist(selectedWorklist);
          fileViewerContext.setAllWorklists(allWorklists);
        }
        onViewFile(file, finalFilteredAndSortedFiles);
      } else {
        console.warn('onViewFile prop missing');
      }
    },
    [onViewFile, finalFilteredAndSortedFiles, selectedWorklist, allWorklists, fileViewerContext],
  );

  const handleStackSelect = useCallback(
    (filesInStack: PreparedFile[], groupKey?: string) => {
      if (!filesInStack || filesInStack.length === 0) return;
      handleSaveScrollPosition(); // Save scroll before navigating

      // When viewing a stack, determine the best navigation target
      const stackName = getStackNameForFiles(filesInStack);
      fileViewerContext.setStackName(stackName);

      // Check if this stack represents a child worklist using the group metadata
      if (groupKey && groupMetadata?.has(groupKey)) {
        const metadata = groupMetadata.get(groupKey);

        // If this group has a worklistId, it represents a child worklist
        if (metadata?.worklistId && allWorklists) {
          const childWorklist = allWorklists.find((w) => w.id === metadata.worklistId);
          if (childWorklist) {
            // Navigate to the child worklist and update sidebar selection
            handleSelectedWorklistIdChange(childWorklist.id);
            console.log(`[handleStackSelect] Navigating to child worklist: ${childWorklist.name}`);
            return;
          }
        }
      }

      // Fallback: If no group metadata or not a child worklist, preserve parent worklist context
      if (selectedWorklist && allWorklists) {
        fileViewerContext.setSelectedWorklist(selectedWorklist);
        fileViewerContext.setAllWorklists(allWorklists);
      }

      // Open the file viewer with the stack
      onViewFile?.(filesInStack[0], filesInStack);
    },
    [
      onViewFile,
      handleSaveScrollPosition,
      getStackNameForFiles,
      fileViewerContext,
      selectedWorklist,
      allWorklists,
      groupMetadata,
      handleSelectedWorklistIdChange,
    ],
  );

  const getTagName = useCallback(
    (tagId: number) => {
      return mergedTagIdToTagMap.get(tagId)?.name ?? 'Unknown Tag';
    },
    [mergedTagIdToTagMap],
  );

  // Header/Sidebar Handlers
  const handleCaseChange = useCallback(
    (event: DropDownListChangeEvent) => {
      const newCaseId = event.value?.value as number | null | undefined;
      if ((typeof newCaseId === 'number' || newCaseId === null) && newCaseId !== selectedCaseId) {
        setSelectedCaseId(newCaseId);
        setSelectedFileForPreview(null);
      }
    },
    [selectedCaseId],
  );

  const handleSortChange = useCallback(
    (event: DropDownListChangeEvent) => {
      const newCriteria = event.value as SortCriteria | undefined;
      if (newCriteria && ['Newest', 'Oldest', 'Name'].includes(newCriteria) && newCriteria !== sortCriteria) {
        setSortCriteria(newCriteria);
      }
    },
    [sortCriteria],
  );

  const handleToggleSidebar = useCallback(() => {
    handleSidebarChange(!(effectiveSidebarExpanded ?? false));
  }, [handleSidebarChange, effectiveSidebarExpanded]);

  const handleGlobalUpload = useCallback(() => {
    // Use the same upload mode approach for both share and patient edit contexts
    // This provides a seamless experience where users can easily get in and out of upload mode
    handleSaveScrollPosition();
    setIsUploadMode(true);
    onUploadModeChange?.(true); // Notify parent component
    setSelectedFileForPreview(null); // Clear any preview when entering upload mode
  }, [handleSaveScrollPosition, onUploadModeChange]);

  const handleFileUploaded = useCallback(
    async (file: File | Blob, metadata?: FileUploadMetadata): Promise<void> => {
      try {
        const finalFilename = metadata?.filename ?? (file instanceof File ? file.name : `upload-${Date.now()}`);
        const fileToSend = new File([file], finalFilename, { type: file.type });

        // Get tags from metadata, fallback to current filter tags, then to initial tags
        const selectedTagIds = metadata?.tags ?? filterTagIds ?? [];
        const captureTimeValue = metadata?.captureTime;
        const eventTimeValue = metadata?.eventTime;
        const description = metadata?.description; // Add description support

        const uploadedFilesResults = await apiClient.filesClient.add(
          [fileToSend],
          false,
          null,
          patientId,
          null,
          activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
        );

        if (uploadedFilesResults && uploadedFilesResults.length > 0) {
          const uploadedFile = uploadedFilesResults[0];
          NotificationsService.displaySuccess(`${finalFilename} uploaded successfully`);

          const realTagIds = selectedTagIds.filter((id: number) => id >= 0);
          if (realTagIds.length > 0) {
            try {
              await apiClient.filesClient.updateFileTags(
                uploadedFile.id,
                realTagIds,
                activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
              );
            } catch (tagError) {
              console.error(`Failed to update tags for file ${uploadedFile.id}:`, tagError);
              NotificationsService.displayError(`Failed to set tags for ${uploadedFile.fileName}. Please edit tags manually.`);
            }
          }

          // Handle description and timestamps
          const patchDoc: { op: string; path: string; value: string | null }[] = [];

          if (description) {
            patchDoc.push({ op: 'replace', path: '/description', value: description });
          }

          if (captureTimeValue) {
            const formattedCaptureTime =
              captureTimeValue instanceof Date ? dayjs(captureTimeValue).toISOString() : typeof captureTimeValue === 'string' ? captureTimeValue : null;
            if (formattedCaptureTime) {
              patchDoc.push({ op: 'replace', path: '/captureTime', value: formattedCaptureTime });
            }
          }

          if (eventTimeValue) {
            const formattedEventTime =
              eventTimeValue instanceof Date ? dayjs(eventTimeValue).toISOString() : typeof eventTimeValue === 'string' ? eventTimeValue : null;
            if (formattedEventTime) {
              patchDoc.push({ op: 'replace', path: '/eventTime', value: formattedEventTime });
            }
          }

          if (patchDoc.length > 0) {
            try {
              await apiClient.filesClient.renameFile(uploadedFile.id, patchDoc);
            } catch (updateError) {
              console.error(`Failed to update file metadata for file ${uploadedFile.id}:`, updateError);
              NotificationsService.displayError(`Failed to set metadata for ${uploadedFile.fileName}. Please edit them manually.`);
            }
          }

          // Refresh the files list and show update indicator
          await fetchData();
          setHasDataUpdates(true);
          setLastUpdateTime(new Date());

          // Auto-hide indicator after 3 seconds
          setTimeout(() => {
            setHasDataUpdates(false);
          }, 3000);
        } else {
          throw new Error('Upload API call succeeded but returned no file data.');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        NotificationsService.displayError('Failed to upload file');
      }
    },
    [apiClient, patientId, activeScheme, fetchData, filterTagIds],
  );

  const handleGenerateQrCode = useCallback(async () => {
    if (!patient) return;

    setIsGeneratingQrCode(true);
    try {
      const linkId = crypto.randomUUID();
      const expireOn = dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss');

      // Create share link with comprehensive context
      let message = 'Patient File Upload';

      if (filterTagIds && filterTagIds.length > 0) {
        // Include tag context for targeted uploads
        if (filterTagIds.length === 1) {
          message = `TAG_ID:${filterTagIds[0]}`;
        } else {
          // Multiple tags - encode them in the message
          message = `TAG_IDS:${filterTagIds.join(',')}`;
        }
      } else if (selectedWorklist) {
        // Include worklist context if available
        message = `WORKLIST:${selectedWorklist.name}`;
      }

      await apiClient.studyShare.addStudyShare(
        {
          id: 0,
          linkId: linkId,
          email: null,
          sharePassword: null,
          shareType: ShareType.Link,
          sharePatientId: patient.id,
          studyShareExams: [],
          expireOn: expireOn,
          dateCreated: '',
          message: message,
          bypassSplashPage: true,
        },
        activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
      );

      setQrLinkId(linkId);
      setIsQrModalVisible(true);

      NotificationsService.displaySuccess('QR code generated successfully! Show this to the mobile device.');
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      NotificationsService.displayError('Failed to generate QR code. Please try again.');
    } finally {
      setIsGeneratingQrCode(false);
    }
  }, [patient, apiClient, activeScheme, filterTagIds, selectedWorklist]);

  // Generate upload context for QR modal
  const uploadContext = useMemo(() => {
    const context: {
      worklistSlug?: string;
      tagPath?: string;
      currentPath?: string;
    } = {};

    // Use hierarchicalPath if available (most specific)
    // Remove 'insight/' prefix if present since QRScanModal will add 'upload/'
    if (hierarchicalPath) {
      const cleanPath = hierarchicalPath.startsWith('insight/') ? hierarchicalPath.substring(8) : hierarchicalPath;
      if (cleanPath) {
        context.currentPath = cleanPath;
      }
      return context;
    }

    // Generate worklist slug if we have a selected worklist
    if (selectedWorklist && selectedWorklist.id !== ALL_FILES_WORKLIST.id) {
      context.worklistSlug = worklistToSlugMap.get(selectedWorklist.id);
    }

    // Generate tag path if we have specific tag filtering
    if (filterTagIds && filterTagIds.length === 1 && mergedFlatTags) {
      const flatTagsMap = new Map(mergedFlatTags.map((t) => [t.id, t]));
      context.tagPath = getTagPath(filterTagIds[0], flatTagsMap);
    }

    return context;
  }, [hierarchicalPath, selectedWorklist, worklistToSlugMap, filterTagIds, mergedFlatTags]);

  const handleQrModalClose = useCallback(() => {
    setIsQrModalVisible(false);
    setQrLinkId('');
  }, []);

  const handleSetFilterTag = useCallback((tagId: number | null) => {
    setFilterTagIds(tagId !== null ? [tagId] : null);
    setSelectedFileForPreview(null); // Reset preview
    console.log('handleSetFilterTag now sets filterTagIds:', tagId !== null ? [tagId] : null);
  }, []); // setFilterTagIds is stable

  const handleNumberOfColumnsChange = useCallback((newColumnCount: number) => {
    const clampedCount = Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, newColumnCount));
    setNumberOfColumns(clampedCount);
  }, []);

  // --- Mocks / Options --- //
  const MOCK_LUNG_CASE = useMemo(
    () => ({
      /* ... as before ... */
    }),
    [patientId],
  );
  const caseOptions = useMemo<CaseOption[]>(() => {
    const realCases = cases?.map((c) => ({ name: c.name, value: c.id })) ?? [];
    const combined = [...realCases];
    const uniqueCases = Array.from(new Map(combined.map((item) => [item.value, item])).values());
    return [ALL_FILES_CASE, ...uniqueCases];
  }, [cases]);

  // Effect to restore scroll position
  useEffect(() => {
    const storedScrollPosition = sessionStorage.getItem(GALLERY_SCROLL_SESSION_KEY);
    if (scrollableContentRef.current && storedScrollPosition) {
      // Ensure content is loaded before trying to scroll, especially for gallery
      // This might need a small delay or a check if items are rendered
      // For stacked view, it might be more reliable as structure is simpler.
      const scrollTop = parseInt(storedScrollPosition, 10);
      if (!isNaN(scrollTop)) {
        // A small timeout can help ensure the DOM is fully ready for scroll manipulation
        // especially if views are complex or data-driven affecting layout.
        // For stacked view, it might be more reliable as structure is simpler.
        setTimeout(() => {
          if (scrollableContentRef.current) {
            scrollableContentRef.current.scrollTop = scrollTop;
            // sessionStorage.removeItem(GALLERY_SCROLL_SESSION_KEY); // remove after use if it's a one-time restore
            // If we want it to persist for subsequent returns before another file view, don't remove immediately
          }
        }, 100); // Adjust delay as needed, or find a more robust way to detect content readiness
      }
    }
    // Clean up the item if it should only be used once per full page load/interaction cycle
    // return () => sessionStorage.removeItem(GALLERY_SCROLL_SESSION_KEY);
  }, [finalFilteredAndSortedFiles, groupedFilesForStackedView]); // Dependencies: re-run when data changes

  // ADDED: Use SignalR for real-time updates with generic handlers
  useSignalRRefresh({
    patientId: typeof patientId === 'number' ? patientId : undefined,
    enableOptimizedUpdates: true, // Enable StartViewingPatient/StopViewingPatient
    enableDebugLogging: true, // Set to true for debugging if needed
    // Legacy specific handlers (keep for backward compatibility)
    onPatientFileUploaded: useCallback(async () => {
      await fetchData();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [fetchData]),
    onWorklistUpdated: useCallback(async () => {
      await refetchWorklists();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [refetchWorklists]),
    onFormUpdated: useCallback(async () => {
      await fetchData();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [fetchData]),
    // NEW: Generic handlers for broader coverage
    onFileDataChanged: useCallback(async () => {
      await fetchData();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [fetchData]), // Handles any file-related changes
    onPatientDataChanged: useCallback(async () => {
      await fetchData();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [fetchData]), // Handles any patient-related changes
    onSystemDataChanged: useCallback(async () => {
      await refetchWorklists();
      setHasDataUpdates(true);
      setLastUpdateTime(new Date());

      // Auto-hide indicator after 3 seconds
      setTimeout(() => {
        setHasDataUpdates(false);
      }, 3000);
    }, [refetchWorklists]), // Handles system-wide changes like worklists

    // NEW: Specific event handlers for more granular control
    onFileChanged: useCallback(
      async (payload: { patientId?: number; fileId?: number; action?: string }) => {
        // Handle specific file change events with more context
        console.log('File changed:', payload);
        await fetchData();
        setHasDataUpdates(true);
        setLastUpdateTime(new Date());

        setTimeout(() => {
          setHasDataUpdates(false);
        }, 3000);
      },
      [fetchData],
    ),

    onExamChanged: useCallback(
      async (payload: { patientId?: number; examId?: number; action?: string; status?: string }) => {
        // Handle exam changes that might affect file visibility
        console.log('Exam changed:', payload);
        await fetchData();
        setHasDataUpdates(true);
        setLastUpdateTime(new Date());

        setTimeout(() => {
          setHasDataUpdates(false);
        }, 3000);
      },
      [fetchData],
    ),
  });

  // Mobile performance optimizations
  const { getOptimizedItemCount, getImageLoadingStrategy, getAnimationSettings } = useMobileOptimizations({
    isMobile,
    isViewingFiles: true,
    enableVirtualization: true,
  });

  // Determine if we should show unstacked view (when viewing a specific folder/worklist)
  const shouldShowUnstackedView = Boolean(isViewingSpecificTag || selectedWorklist);

  // Create the collapsed worklist component for toolbar integration
  // Don't show in share mode since we have the filter button in tab navigation
  const SlotToolbarPrefix = useMemo(() => {
    if (effectiveSidebarExpanded ?? false) return undefined;
    if (shareContext?.isShareMode) return undefined; // Don't show collapsed sidebar in share mode

    const handleShowSidebar = () => handleSidebarChange(true);
    const worklistName = selectedWorklist?.name || (isViewingSpecificTag && parseHierarchicalPath.currentTag?.name) || 'All Files';
    return <WorklistSidebarCollapsed handleShowSidebar={handleShowSidebar} selectedWorklistName={worklistName} />;
  }, [
    effectiveSidebarExpanded,
    shareContext?.isShareMode,
    selectedWorklist?.name,
    isViewingSpecificTag,
    parseHierarchicalPath.currentTag?.name,
    handleSidebarChange,
  ]);

  // --- Render Logic --- //
  if (isLoadingPatientFiles && preparedFiles.length === 0) {
    return <ProgressSpinnerOverlay show={true} />;
  }
  if (!patient && !isLoadingPatientFiles) {
    return <div>Error loading patient data or patient not found.</div>;
  }

  return (
    <>
      <UploadPipelineProvider trackerSource={`Web-PatientFiles`}>
        <PageWrapper id="patient-files-page-wrapper">
          <PageLayout>
            <SidebarContainer expanded={effectiveSidebarExpanded ?? false}>
              {(effectiveSidebarExpanded ?? false) && (
                <>
                  {patientId && (
                    <WorklistSidebar
                      allWorklists={allWorklists}
                      allCounts={allCounts}
                      selectedWorklistId={selectedWorklistId}
                      showSidebar={true}
                      currentDataState={null}
                      currentColumnsState={null}
                      onShowSidebarChange={handleSidebarChange}
                      onSelectedWorklistIdChange={handleSelectedWorklistIdChange}
                      onRefreshClick={handleWorklistRefresh}
                      onCreateWorklist={handleCreateWorklist}
                      onUpdateWorklist={handleUpdateWorklist}
                      onDeleteWorklist={handleDeleteWorklist}
                      filterType="tag"
                      patientId={patientId}
                      isReadOnly={isReadOnly}
                    />
                  )}
                </>
              )}
            </SidebarContainer>

            <ContentAndPreviewLayout showPreview={!!selectedFileForPreview && !isMobile && !isUploadMode} isMobile={isMobile}>
              {/* Only render header if it has content to show */}
              {(() => {
                // Replicate the same logic from PatientFilesHeader to check if it should render
                const hasLeftContent = SlotToolbarPrefix || hasDataUpdates;
                const hasRightContent = !isMobile && !isUploadMode; // Column slider
                const hasUploadButton = true; // Upload button is always available
                const hasAnyContent = hasLeftContent || hasRightContent || isUploadMode || hasUploadButton;

                return hasAnyContent ? (
                  <PatientFilesHeader
                    sortCriteria={sortCriteria}
                    onSortCriteriaChange={handleSortChange}
                    numberOfColumns={numberOfColumns}
                    onNumberOfColumnsChange={setNumberOfColumns}
                    onToggleSidebar={() => handleSidebarChange(!(effectiveSidebarExpanded ?? false))}
                    caseOptions={caseOptions}
                    selectedCaseId={selectedCaseId}
                    onCaseChange={handleCaseChange}
                    isLoading={totalLoading}
                    isMobile={isMobile}
                    onUploadClick={isUploadMode ? handleExitUploadMode : handleGlobalUpload}
                    patientId={patientId}
                    hierarchicalPath={hierarchicalPath}
                    mergedFlatTags={mergedFlatTags}
                    mergedTagNameToIdMap={mergedTagNameToIdMap}
                    selectedWorklist={selectedWorklist}
                    allWorklists={allWorklists}
                    stackName={undefined}
                    shareContext={shareContext}
                    slotToolbarPrefix={SlotToolbarPrefix}
                    hasDataUpdates={hasDataUpdates}
                    lastUpdateTime={lastUpdateTime}
                    isUploadMode={isUploadMode}
                  />
                ) : null;
              })()}

              <MainContentArea showPreview={!!selectedFileForPreview && !isMobile && !isUploadMode} isMobile={isMobile}>
                {isUploadMode ? (
                  <ScrollableContentArea data-scroll-container="true">
                    <FormProvider {...uploadFormMethods}>
                      <UploadModeContainer>
                        <UploadModeHeader>
                          <h4>{shareContext?.isShareMode ? 'Upload Files via Share Link' : 'Upload Files'}</h4>
                        </UploadModeHeader>
                        <Upload
                          onFileUploaded={handleFileUploaded}
                          patientId={patientId}
                          onGenerateQrCode={handleGenerateQrCode}
                          isGeneratingQrCode={isGeneratingQrCode}
                          availableTags={mergedHierarchicalTags || []}
                          initialTagIds={initialTagIdsForUpload}
                          isLoadingTags={isLoadingMergedTags}
                          filterTagName={null}
                          hierarchicalPath={hierarchicalPath}
                          worklistContext={selectedWorklist?.name || null}
                          onUploadComplete={handleExitUploadMode}
                          showDescriptionField={true}
                        />
                      </UploadModeContainer>
                    </FormProvider>
                  </ScrollableContentArea>
                ) : (
                  <Routes>
                    <Route
                      path="*"
                      element={
                        <>
                          <ScrollableContentArea ref={scrollableContentRef} data-scroll-container>
                            <PatientFilesStackedView
                              groupedFiles={groupedFilesForStackedView}
                              groupMetadata={groupMetadata}
                              numberOfColumns={numberOfColumns}
                              isLoading={totalLoading && groupedFilesForStackedView.size === 0}
                              onStackSelect={handleStackSelect}
                              tagIdToTagMap={mergedTagIdToTagMap}
                              onFileSelect={handleFileSelect}
                              onFileView={handleViewFile}
                              sortCriteria={sortCriteria}
                            />
                          </ScrollableContentArea>
                          {selectedFileForPreview && !isMobile && (
                            <PatientFilePreviewEdit
                              selectedFile={selectedFileForPreview}
                              onView={() => {
                                if (selectedFileForPreview && onViewFile) {
                                  onViewFile(selectedFileForPreview, [selectedFileForPreview]);
                                } else {
                                  console.warn('Cannot trigger onView - missing file or handler');
                                }
                              }}
                              onDownload={async (file) => console.log('Download placeholder', file)}
                              onDelete={
                                isReadOnly
                                  ? async () => {
                                      /* No-op in readonly mode */
                                    }
                                  : handleDeleteFileFromPreview
                              }
                              onRename={
                                isReadOnly
                                  ? async () => {
                                      /* No-op in readonly mode */
                                    }
                                  : async (file, name) => console.log('Rename placeholder', file, name)
                              }
                              onUpdateDescription={
                                isReadOnly
                                  ? async () => {
                                      /* No-op in readonly mode */
                                    }
                                  : async (file, desc) => console.log('Update description placeholder')
                              }
                              onUpdateTimestamps={
                                isReadOnly
                                  ? async () => {
                                      /* No-op in readonly mode */
                                    }
                                  : handleUpdateTimestamps
                              }
                              allTagsFlat={mergedFlatTags}
                              tagIdToTagMap={mergedTagIdToTagMap}
                              includeThumbnail={true} // Show thumbnail in standalone preview mode
                              showFileDetails={true} // Show full file details in standalone mode
                              showThumbnailGallery={true} // Show thumbnail gallery and actions in standalone mode
                              allHierarchicalTags={mergedHierarchicalTags}
                              onUpdateTags={
                                isReadOnly
                                  ? async () => {
                                      /* No-op in readonly mode */
                                    }
                                  : handleUpdateTags
                              }
                              areTagsLoading={isLoadingMergedTags}
                              formSubmissions={[]}
                              areFormSubmissionsLoading={false}
                              onFormUpdated={async () => console.log('Form updated placeholder')}
                              selectedWorklist={selectedWorklist}
                              allWorklists={allWorklists}
                              isReadOnly={isReadOnly}
                            />
                          )}
                        </>
                      }
                    />
                  </Routes>
                )}
              </MainContentArea>
            </ContentAndPreviewLayout>
          </PageLayout>
        </PageWrapper>
      </UploadPipelineProvider>

      {/* QR Code Modal for upload mode */}
      {isQrModalVisible && qrLinkId && (
        <QRScanModal linkId={qrLinkId} onClose={handleQrModalClose} show={isQrModalVisible} examId={null} uploadContext={uploadContext} />
      )}
    </>
  );
};

const ContentAndPreviewLayout = styled.div<{ showPreview: boolean; isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  flex-grow: 1; /* Allow this layout to fill the PageLayout */

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: auto;
    overflow: visible; // Allow overflow on mobile if needed
    width: 100%;
    max-width: 100%;
  }
`;

const MainContentArea = styled.div<{ showPreview: boolean; isMobile: boolean }>`
  display: flex;
  flex-grow: 1;
  overflow: hidden; /* Prevent this container from scrolling */
  min-height: 0; /* Allow flexbox shrinking */

  ${({ showPreview, isMobile }) =>
    showPreview && !isMobile
      ? `
        /* Files area takes remaining space, preview takes fixed 350px */
        > *:first-child {
          flex: 1;
          min-width: 0; /* Allow shrinking */
        }
        > *:last-child {
          flex: 0 0 350px;
          width: 350px;
        }
      `
      : `
        /* Files area takes full width when no preview */
        > * {
          flex: 1;
        }
      `}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    height: auto;
    overflow: visible;

    /* Ensure full width usage on mobile */
    > * {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const ScrollableContentArea = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto; // This should handle the scrolling
  overflow-x: hidden; // Prevent horizontal scroll
  //background-color: #f0f2f5;
  box-sizing: border-box;
  will-change: transform; /* Enable GPU acceleration for better scrolling performance */
  overscroll-behavior: contain; /* Prevent scroll chaining to parent elements */
  touch-action: pan-y; /* Allow only vertical scrolling on touch devices */
  height: 100%; /* Ensure it takes full height of parent */
`;

// New Layout Components
const PageLayout = styled.div`
  display: flex;
  flex-grow: 1; /* Fill space below header */
  position: relative; /* For mobile sidebar positioning */
  overflow: hidden; /* Prevent this container from scrolling */
  height: 100%; /* Ensure it takes full height */

  /* Mobile adjustments */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
  }
`;

const SidebarContainer = styled.div<{ expanded: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  width: ${({ expanded }) => (expanded ? '280px' : '0px')}; /* Increased width for better usability */
  min-height: 400px; /* Ensure minimum height even when no files */
  height: 100vh; /* Take full viewport height to stick */
  max-height: 100vh; /* Prevent growing beyond viewport */
  position: sticky; /* Stick to viewport */
  top: 0; /* Stick to top of viewport */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); /* Smoother easing curve */
  border-right: ${({ expanded, theme }) => (expanded ? `1px solid ${theme.colors.borderBase}` : 'none')};
  background-color: ${({ theme }) => theme.colors.palette.white}; /* White background for cleaner look */
  box-shadow: ${({ expanded }) => (expanded ? '2px 0 8px rgba(0, 0, 0, 0.1)' : 'none')};
  overflow: hidden;
  z-index: 10; /* Ensure it stays above content */
  overscroll-behavior: none; /* Prevent scroll chaining */

  /* Add slide-in animation */
  transform: ${({ expanded }) => (expanded ? 'translateX(0)' : 'translateX(-100%)')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};

  /* Mobile adjustments - take content space like desktop */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${({ expanded }) =>
      expanded
        ? `
          position: relative;
          width: 100%;
          height: auto;
          min-height: 400px;
          z-index: 1;
          background-color: white;
          border-right: none;
          border-bottom: 1px solid #e0e0e0;
          border-radius: 0;
          transform: translateX(0);
          opacity: 1;
          box-shadow: none;
          overflow-y: visible;
        `
        : `
          position: relative;
          width: 0;
          height: 0;
          min-height: 0;
          z-index: -1;
          transform: translateX(-100%);
          opacity: 0;
          overflow: hidden;
        `}
  }

  /* No backdrop needed for content-space approach */
`;

// Add a top-level container if needed, or ensure parent provides flex column layout
const PageWrapper = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
`;

const UploadModeContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  > div {
    padding: 0.5rem;
  }
`;

const UploadModeHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};

  h4 {
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const PatientFilesWithProvider: React.FC<PatientFilesProps> = (props) => {
  return (
    <FileViewerProvider>
      <PatientFilesComponent {...props} />
    </FileViewerProvider>
  );
};

export const PatientFiles = React.memo(PatientFilesWithProvider);
